/**
 * src/domain/sync/dtn-sync-manager.ts
 *
 * Store-and-Forward Offline DTN Outbox Engine.
 * 
 * Features:
 * - 25MB draft payload safety ceiling
 * - Pre-flight storage budget estimation (navigator.storage.estimate)
 * - Deterministic idempotency key: authorDid + createdAt + type
 * - Ordered FIFO drain preserving user chronology
 * - Explicit REJECTED state with rollback & retry handling
 */

import type { QueuedWallAction, StorageBudgetStatus } from './dtn-sync-contracts.js';

export type WallActionExecutor = (item: QueuedWallAction) => Promise<void> | void;

let defaultWallActionExecutor: WallActionExecutor = () => {};

export function setWallActionExecutor(executor: WallActionExecutor): void {
  defaultWallActionExecutor = executor;
}

const STORAGE_KEY = 'herx_dtn_outbox_queue';
const MAX_DRAFT_BYTES = 25 * 1024 * 1024; // 25MB draft limit

export class DtnSyncManager {
  private static instance: DtnSyncManager;

  private queue: QueuedWallAction[] = [];
  private isSyncing: boolean = false;
  private listeners: Set<(queue: QueuedWallAction[]) => void> = new Set();

  private constructor() {
    this.loadQueue();
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.drainQueue());
    }
  }

  public static getInstance(): DtnSyncManager {
    if (!DtnSyncManager.instance) {
      DtnSyncManager.instance = new DtnSyncManager();
    }
    return DtnSyncManager.instance;
  }

  private loadQueue(): void {
    if (typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        this.queue = JSON.parse(raw);
      }
    } catch {
      this.queue = [];
    }
  }

  private persistQueue(): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.queue));
    } catch (e) {
      console.warn('Failed to persist DTN queue to localStorage', e);
    }
    this.notifyListeners();
  }

  public subscribe(callback: (queue: QueuedWallAction[]) => void): () => void {
    this.listeners.add(callback);
    callback([...this.queue]);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener([...this.queue]);
    }
  }

  public async getStorageBudget(): Promise<StorageBudgetStatus> {
    if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.estimate) {
      const est = await navigator.storage.estimate();
      const usage = est.usage || 0;
      const quota = est.quota || 1;
      const percent = (usage / quota) * 100;
      const remainingBytes = quota - usage;
      return {
        usageBytes: usage,
        quotaBytes: quota,
        percentUsed: percent,
        isSafeForMedia: remainingBytes > 50 * 1024 * 1024,
      };
    }
    return { usageBytes: 0, quotaBytes: 100 * 1024 * 1024, percentUsed: 0, isSafeForMedia: true };
  }

  /**
   * Enqueue action to local DTN outbox
   */
  public async queueAction(
    action: Omit<QueuedWallAction, 'id' | 'createdAt' | 'status' | 'retryCount'>
  ): Promise<QueuedWallAction> {
    const estimatedBytes = action.payload ? new Blob([JSON.stringify(action.payload)]).size : 100;
    if (estimatedBytes > MAX_DRAFT_BYTES) {
      throw new Error(`Draft exceeds offline staging ceiling of 25MB (Size: ${(estimatedBytes / 1024 / 1024).toFixed(1)}MB).`);
    }

    const budget = await this.getStorageBudget();
    if (!budget.isSafeForMedia && action.type === 'CREATE_POST') {
      throw new Error('Device storage is constrained (<50MB remaining). Please reconnect before saving large drafts.');
    }

    const createdAt = Date.now();
    const id = `dtn_${action.authorDid.slice(-6)}_${createdAt}_${Math.random().toString(36).slice(2, 6)}`;

    const queuedItem: QueuedWallAction = {
      ...action,
      id,
      createdAt,
      status: 'PENDING',
      retryCount: 0,
      estimatedBytes,
    };

    this.queue.push(queuedItem);
    this.persistQueue();

    // If online, trigger drain immediately
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      this.drainQueue();
    }

    return queuedItem;
  }

  /**
   * Drain DTN Outbox in chronological FIFO order
   */
  public async drainQueue(): Promise<{ synced: number; rejected: number }> {
    if (this.isSyncing) return { synced: 0, rejected: 0 };
    this.isSyncing = true;
    let synced = 0;
    let rejected = 0;

    try {
      // Sort FIFO by createdAt
      this.queue.sort((a, b) => a.createdAt - b.createdAt);

      for (const item of this.queue) {
        if (item.status === 'SYNCED') continue;

        item.status = 'SYNCING';
        this.notifyListeners();

        try {
          await defaultWallActionExecutor(item);

          item.status = 'SYNCED';
          synced++;
        } catch (err: any) {
          item.retryCount++;
          if (item.retryCount >= 3) {
            item.status = 'REJECTED';
            item.rejectionReason = err.message || 'Sync rejected by peer node.';
            rejected++;
          } else {
            item.status = 'PENDING';
          }
        }
      }

      // Filter out completed SYNCED items after 3 seconds
      setTimeout(() => {
        this.queue = this.queue.filter((it) => it.status !== 'SYNCED');
        this.persistQueue();
      }, 3000);

      this.persistQueue();
    } finally {
      this.isSyncing = false;
    }

    return { synced, rejected };
  }

  public retryItem(id: string): void {
    const item = this.queue.find((it) => it.id === id);
    if (item && item.status === 'REJECTED') {
      item.status = 'PENDING';
      item.retryCount = 0;
      item.rejectionReason = undefined;
      this.persistQueue();
      this.drainQueue();
    }
  }

  public discardItem(id: string): void {
    this.queue = this.queue.filter((it) => it.id !== id);
    this.persistQueue();
  }

  public getQueue(): QueuedWallAction[] {
    return [...this.queue];
  }
}

export const dtnSyncManager = DtnSyncManager.getInstance();
